import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getService, listServices } from '../../../api/companyRecords';
import { getServicePathSegment, resolveServiceByRouteParam } from '../utils/serviceSlug';

/**
 * Resolves /services/:param (slug or legacy id) to a service document.
 * Redirects legacy id URLs to the canonical name slug when possible.
 */
export default function useResolvedServiceRoute(routeParam) {
  const { companyData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({
    status: 'loading',
    service: null,
    firestoreId: null,
  });

  useEffect(() => {
    if (!companyData?.id || !routeParam) {
      setState({ status: 'idle', service: null, firestoreId: null });
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      setState({ status: 'loading', service: null, firestoreId: null });

      try {
        const services = (await listServices({ companyId: companyData.id }))
          .filter((entry) => !entry._init);
        if (cancelled) return;

        const match = resolveServiceByRouteParam(services, routeParam);
        if (!match) {
          setState({ status: 'not_found', service: null, firestoreId: null });
          return;
        }

        const canonical = getServicePathSegment(match);
        if (canonical && routeParam !== canonical) {
          const nextPath = location.pathname.replace(
            `/services/${routeParam}`,
            `/services/${canonical}`
          );
          navigate(`${nextPath}${location.search}${location.hash}`, { replace: true });
          return;
        }

        const live = await getService({ companyId: companyData.id, id: match.id });
        if (cancelled) return;
        if (!live) {
          setState({ status: 'not_found', service: null, firestoreId: null });
          return;
        }
        setState({
          status: 'ready',
          service: live,
          firestoreId: live.id,
        });
      } catch (error) {
        console.error('Error resolving service route:', error);
        if (!cancelled) {
          setState({ status: 'not_found', service: null, firestoreId: null });
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [companyData?.id, routeParam, navigate, location.pathname, location.search, location.hash]);

  return state;
}
